import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { getVendorScorecard } from "@/lib/vendor-scorecard.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { ScopeBar } from "@/components/scope-bar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, AlertTriangle, Camera, Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/receiving/scorecard")({
  component: ScorecardPage,
});

function scoreTone(score: number | null) {
  if (score == null) return "outline" as const;
  if (score >= 90) return "default" as const;
  if (score >= 70) return "secondary" as const;
  return "destructive" as const;
}

function ScorecardPage() {
  const { tenantId, facilityId } = useScope();
  const [days, setDays] = useState("90");
  const getFn = useServerFn(getVendorScorecard);
  const { data, isLoading } = useQuery({
    queryKey: ["vendor-scorecard", tenantId, facilityId, days],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => getFn({ data: { tenantId: tenantId!, facilityId: facilityId!, sinceDays: parseInt(days, 10) } }),
  });
  const rows = data?.rows ?? [];
  const avgScore = rows.length > 0
    ? Math.round(rows.filter((r) => r.score != null).reduce((a, r) => a + (r.score ?? 0), 0) / Math.max(1, rows.filter((r) => r.score != null).length))
    : null;
  const totalVariances = rows.reduce((a, r) => a + r.shortages + r.overages, 0);
  const totalPhotoPct = rows.length > 0
    ? Math.round(rows.filter((r) => r.photo_pct != null).reduce((a, r) => a + (r.photo_pct ?? 0), 0) / Math.max(1, rows.filter((r) => r.photo_pct != null).length))
    : null;

  return (
    <>
      <PageHeader
        title="Vendor Scorecard"
        description="Receiving accuracy, on-time arrivals, and photo-evidence coverage by brand."
        actions={
          <div className="flex items-center gap-2">
            <ScopeBar />
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Link to="/app/receiving" className="text-sm text-muted-foreground hover:text-foreground">← Receiving</Link>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="pt-6">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" />Avg score</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{avgScore ?? "—"}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />Total variances</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{totalVariances}</div>
          </CardContent></Card>
          <Card><CardContent className="pt-6">
            <div className="text-xs text-muted-foreground flex items-center gap-1"><Camera className="h-3.5 w-3.5" />Photo coverage</div>
            <div className="text-2xl font-semibold mt-1 tabular-nums">{totalPhotoPct != null ? `${totalPhotoPct}%` : "—"}</div>
          </CardContent></Card>
        </div>

        <div className="border rounded-lg bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brand</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead className="text-right">ASNs</TableHead>
                <TableHead className="text-right"><Clock className="inline h-3.5 w-3.5 mr-1" />On-time</TableHead>
                <TableHead className="text-right">Accuracy</TableHead>
                <TableHead className="text-right">Shortages</TableHead>
                <TableHead className="text-right">Overages</TableHead>
                <TableHead className="text-right">Photos</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : rows.length > 0 ? rows.map((r) => (
                <TableRow key={r.customer_id}>
                  <TableCell className="font-medium">{r.customer_name}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant={scoreTone(r.score)}>{r.score ?? "—"}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.asn_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.on_time_pct != null ? `${r.on_time_pct}%` : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.accuracy_pct != null ? `${r.accuracy_pct}%` : "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.shortages > 0 ? <span className="text-destructive">{r.shortages} ({r.shortage_units}u)</span> : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.overages > 0 ? <span>{r.overages} ({r.overage_units}u)</span> : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.photo_pct != null ? `${r.photo_pct}%` : "—"}</TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No receiving activity in this window.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}